import { useState, useCallback } from 'react';
import { saveAs } from 'file-saver';
import { getSimulations, exportInstitutionReport } from '@/api/dashboardService';
import { parseApiError } from '@/utils/errorHandler';
import type { simulation } from '@/features/dashboard/types.api';
import type { parsedApiError } from '@/types/api/errors.api';

export const useInstitutionReport = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<parsedApiError | null>(null);
    const [simulations, setSimulations] = useState<simulation[]>([]);
    const [targetInstitutionId, setTargetInstitutionId] = useState<number | string | null>(null);

    const openReportModal = useCallback(async (institutionId: number | string) => {
        setTargetInstitutionId(institutionId);
        setIsModalOpen(true);
        setIsLoading(true);
        setError(null);
        try {
            const sims = await getSimulations(null, String(institutionId));
            setSimulations(sims);
        } catch (err) {
            setError(parseApiError(err));
        } finally {
            setIsLoading(false);
        }
    }, []);

    const closeReportModal = useCallback(() => {
        setIsModalOpen(false);
        setTargetInstitutionId(null);
        setSimulations([]);
    }, []);

    const handleDownload = useCallback(async (simulationId: string) => {
        if (!targetInstitutionId) return;
        setIsLoading(true);
        setError(null);
        try {
            const blob = await exportInstitutionReport(targetInstitutionId, simulationId);
            const targetSim = simulations.find(s => String(s.id) === simulationId);
            const fileName = `reporte_institucion_${targetInstitutionId}_${targetSim?.name.replace(/\s+/g, '_') || simulationId}.xlsx`;
            saveAs(blob, fileName);
            closeReportModal();
        } catch (err) {
            setError(parseApiError(err));
        } finally {
            setIsLoading(false);
        }
    }, [targetInstitutionId, simulations, closeReportModal]);

    return {
        isModalOpen,
        isLoading,
        error,
        simulations,
        openReportModal,
        closeReportModal,
        handleDownload,
    };
};
